type MultipartSession = {
  key: string;
  uploadId: string;
  partSize: number;
  partCount: number;
};

type UploadedPart = { partNumber: number; size: number };

export type ResumableUploadApi = {
  createSession: (file: File, contentType: string) => Promise<MultipartSession>;
  uploadPart: (
    session: MultipartSession,
    partNumber: number,
    chunk: Blob,
    onChunkProgress: (loaded: number) => void
  ) => Promise<void>;
  getUploadedParts: (session: MultipartSession) => Promise<UploadedPart[]>;
};

export type ResumableUploadOptions = {
  /** Called with the total bytes accepted so far. */
  onBytes?: (loaded: number) => void;
  /** Parts sent at once. Higher saturates the uplink; lower is gentler. */
  concurrency?: number;
  /** Attempts per part before the upload is abandoned. */
  maxRetries?: number;
};

const SESSION_STORAGE_KEY = "gcp:resumable-uploads";
/** Sessions are dropped after this long. */
const SESSION_TTL_MS = 24 * 60 * 60 * 1000;

/**
 * Identifies a file well enough to match it against a stored session. Contents
 * are not hashed — that would mean reading the whole file before starting.
 */
const fingerprintFile = (file: File): string =>
  `${file.name}:${file.size}:${file.lastModified}`;

type StoredSessions = Record<string, MultipartSession & { storedAt: number }>;

const readStoredSessions = (): StoredSessions => {
  try {
    const raw = localStorage.getItem(SESSION_STORAGE_KEY);
    const sessions: StoredSessions = raw ? JSON.parse(raw) : {};
    const now = Date.now();

    return Object.fromEntries(
      Object.entries(sessions).filter(
        ([, session]) => now - session.storedAt < SESSION_TTL_MS
      )
    );
  } catch {
    // Private-mode or corrupted storage: resuming is an optimisation, so a
    // failure here only means the upload starts from the beginning.
    return {};
  }
};

const writeStoredSessions = (sessions: StoredSessions): void => {
  try {
    localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(sessions));
  } catch {
    /* storage unavailable — carry on without resume support */
  }
};

const loadSession = (file: File): MultipartSession | null =>
  readStoredSessions()[fingerprintFile(file)] ?? null;

const saveSession = (file: File, session: MultipartSession): void => {
  const sessions = readStoredSessions();
  sessions[fingerprintFile(file)] = { ...session, storedAt: Date.now() };
  writeStoredSessions(sessions);
};

const clearSession = (file: File): void => {
  const sessions = readStoredSessions();
  delete sessions[fingerprintFile(file)];
  writeStoredSessions(sessions);
};

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * A 4xx means the request itself is wrong and will fail again; anything else —
 * including a dropped connection, which has no response at all — is worth
 * another attempt.
 */
const isRetryable = (error: any): boolean => {
  const status = error?.response?.status;
  return !status || status === 408 || status === 429 || status >= 500;
};

/**
 * Runs `worker` over the queue with at most `concurrency` in flight, so a
 * 300 MB file does not open 38 connections at once.
 */
const runPooled = async <T>(
  items: T[],
  concurrency: number,
  worker: (item: T) => Promise<void>
): Promise<void> => {
  const queue = [...items];

  const runners = Array.from(
    { length: Math.min(concurrency, queue.length) },
    async () => {
      let item = queue.shift();
      while (item !== undefined) {
        await worker(item);
        item = queue.shift();
      }
    }
  );

  await Promise.all(runners);
};

export const uploadFileResumable = async (
  file: File,
  contentType: string,
  api: ResumableUploadApi,
  options: ResumableUploadOptions = {}
): Promise<{ key: string; uploadId: string }> => {
  const { onBytes, concurrency = 3, maxRetries = 4 } = options;

  let session = loadSession(file);
  let uploadedParts: UploadedPart[] = [];

  if (session) {
    try {
      // Ask what survived the interruption. If the session is gone (expired or
      // aborted), fall through and start a fresh one.
      uploadedParts = await api.getUploadedParts(session);
    } catch {
      clearSession(file);
      session = null;
    }
  }

  if (!session) {
    session = await api.createSession(file, contentType);
    saveSession(file, session);
  }

  const activeSession = session;
  const { partSize, partCount } = activeSession;

  const completedBytes = new Map<number, number>(
    uploadedParts.map((part) => [part.partNumber, part.size])
  );
  // Bytes in flight, per part, so progress moves smoothly with parallel parts.
  const inFlightBytes = new Map<number, number>();

  const reportProgress = () => {
    if (!onBytes) return;

    const sum = (values: Iterable<number>) =>
      Array.from(values).reduce((total, value) => total + value, 0);

    onBytes(sum(completedBytes.values()) + sum(inFlightBytes.values()));
  };

  reportProgress();

  const pendingPartNumbers = Array.from(
    { length: partCount },
    (_, index) => index + 1
  ).filter((partNumber) => !completedBytes.has(partNumber));

  await runPooled(pendingPartNumbers, concurrency, async (partNumber) => {
    const start = (partNumber - 1) * partSize;
    const chunk = file.slice(start, Math.min(start + partSize, file.size));

    for (let attempt = 1; ; attempt++) {
      try {
        await api.uploadPart(activeSession, partNumber, chunk, (loaded) => {
          inFlightBytes.set(partNumber, loaded);
          reportProgress();
        });

        inFlightBytes.delete(partNumber);
        completedBytes.set(partNumber, chunk.size);
        reportProgress();
        return;
      } catch (error) {
        inFlightBytes.delete(partNumber);
        reportProgress();

        if (attempt > maxRetries || !isRetryable(error)) throw error;

        // Back off before retrying: a part usually fails because the connection
        // dropped, and retrying instantly just fails again.
        await delay(Math.min(1000 * 2 ** (attempt - 1), 15000));
      }
    }
  });

  // Only forget the session once every part is in place; if completing fails
  // the next attempt still resumes rather than re-uploading.
  const finished = {
    key: activeSession.key,
    uploadId: activeSession.uploadId,
  };
  clearSession(file);

  return finished;
};
